
import { Stack, TextField, Text, mergeStyleSets, Checkbox, IconButton, IColor, ColorPicker, IColorPickerStyles, DefaultButton } from '@fluentui/react';
import React from 'react';
import { IMetaTextColor } from '../data-structures/interfaces';
const selectedColor = "var(--db-hover-bg)";

const baseStyle = {
    // borderBottomColor: theme.palette.neutralLight,
    // borderBottomWidth: 0.5,
    // borderBottomStyle: 'solid',
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 15,
    paddingRight: 15,
    width: "100%",
}
const orderedListStyles = mergeStyleSets({
    selected: {
        ...baseStyle,
        backgroundColor: selectedColor,
    },
    standard: {
        ...baseStyle,
    }
})

const pillStyle: React.CSSProperties = {
    boxSizing: "border-box",
    padding: "4px 8px 5px 8px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "flex",
    borderRadius: 16,
    height: 24,
    alignItems: "center",
    whiteSpace: "nowrap",
    margin: 4,
    marginLeft: 14
};

const colorPickerStyles: Partial<IColorPickerStyles> = {
    panel: { padding: 12 },
    root: {
        maxWidth: 352,
        minWidth: 352,
    },
    colorRectangle: { height: 268 },
};

interface RgbColor {
    r: number;
    g: number;
    b: number;
}

interface HslColor {
    h: number;
    s: number;
    l: number;
}

const getTextColor = (hexcolor?: string) => {
    if (!hexcolor) return "white";

    // if (hexcolor.slice(0, 1) === '#') {
    //     hexcolor = hexcolor.slice(1);
    // }

    // const r = parseInt(hexcolor.substr(0, 2), 16);
    // const g = parseInt(hexcolor.substr(2, 2), 16);
    // const b = parseInt(hexcolor.substr(4, 2), 16);
    // const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    // return (yiq >= 128) ? "black" : "white";

    // Function to convert hex color to RGB
    const hexToRgb = (hex: string): RgbColor | null => {
        const hexValue = hex.startsWith('#') ? hex.slice(1) : hex;
        if (!/^[0-9a-fA-F]{6}$/.test(hexValue)) {
            console.error("Invalid hex color format");
            return null;
        }
        const r = parseInt(hexValue.substring(0, 2), 16);
        const g = parseInt(hexValue.substring(2, 4), 16);
        const b = parseInt(hexValue.substring(4, 6), 16);
        return { r, g, b };
    };

    // Function to convert RGB to hex color
    const rgbToHex = ({ r, g, b }: RgbColor): string => {
        return `#${[r, g, b]
            .map((c) => c.toString(16).padStart(2, '0'))
            .join('')}`;
    };

    // Function to convert RGB to HSL
    const rgbToHsl = ({ r, g, b }: RgbColor): HslColor => {
        const rNorm = r / 255;
        const gNorm = g / 255;
        const bNorm = b / 255;

        const max = Math.max(rNorm, gNorm, bNorm);
        const min = Math.min(rNorm, gNorm, bNorm);
        let h = 0;
        let s = 0;
        const l = (max + min) / 2;

        if (max !== min) {
            const d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            switch (max) {
                case rNorm:
                    h = (gNorm - bNorm) / d + (gNorm < bNorm ? 6 : 0);
                    break;
                case gNorm:
                    h = (bNorm - rNorm) / d + 2;
                    break;
                case bNorm:
                    h = (rNorm - gNorm) / d + 4;
                    break;
            }
            h /= 6;
        }

        return { h: h * 360, s: s * 100, l: l * 100 };
    };

    // Function to convert HSL to RGB
    const hslToRgb = ({ h, s, l }: HslColor): RgbColor => {
        s /= 100;
        l /= 100;

        const k = (n: number) => (n + h / 30) % 12;
        const a = s * Math.min(l, 1 - l);
        const f = (n: number) =>
            l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

        return {
            r: Math.round(255 * f(0)),
            g: Math.round(255 * f(8)),
            b: Math.round(255 * f(4)),
        };
    };

    // Calculate relative luminance (per WCAG 2.0)
    const luminance = (rgb: RgbColor): number => {
        const a = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((v) =>
            v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
        );
        return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
    };

    // Calculate contrast ratio (per WCAG 2.0)
    const contrastRatio = (color1: RgbColor, color2: RgbColor): number => {
        const lum1 = luminance(color1);
        const lum2 = luminance(color2);
        const brightest = Math.max(lum1, lum2);
        const darkest = Math.min(lum1, lum2);
        return (brightest + 0.05) / (darkest + 0.05);
    };

    const rgbBackground = hexToRgb(hexcolor);
    if (!rgbBackground) {
        return '#000000'; // Default to black if background color is invalid
    }

    const hslBackground = rgbToHsl(rgbBackground);
    const lumBackground = luminance(rgbBackground);
    const minContrastRatio = 4.5;
    const isLightBackground = lumBackground > 0.5;
    const maxIterations = 20; // Limit iterations to prevent infinite loops

    let contrastingHsl: HslColor = { ...hslBackground };
    let contrastingRgb: RgbColor;
    let iterations = 0;

    if (isLightBackground) {
        // Try darkening
        while (iterations < maxIterations && contrastingHsl.l > 0) {
            contrastingHsl.l -= 5; // Decrease lightness in steps
            contrastingRgb = hslToRgb(contrastingHsl);
            if (contrastRatio(rgbBackground, contrastingRgb) >= minContrastRatio) {
                return rgbToHex(contrastingRgb);
            }
            iterations++;
        }
        return '#000000'; // Fallback to black if no sufficient variant found
    } else {
        // Try lightening
        while (iterations < maxIterations && contrastingHsl.l < 100) {
            contrastingHsl.l += 5; // Increase lightness in steps
            contrastingRgb = hslToRgb(contrastingHsl);
            if (contrastRatio(rgbBackground, contrastingRgb) >= minContrastRatio) {
                return rgbToHex(contrastingRgb);
            }
            iterations++;
        }
        return '#FFFFFF'; // Fallback to white if no sufficient variant found
    }
};

interface OrderedListItemProps {
    value: string | IMetaTextColor,
    index: number,
    isSelected: boolean,
    setIsSelected: (index: number, value?: boolean) => void,
    setFieldValue: (index: number, value: string, newHexColor?: string) => void,
    onEnter: () => void,
    isColorPicker: boolean,
    metaDataTextColorValue?: IMetaTextColor
};

const OrderedListItem: React.FC<OrderedListItemProps> = ({
    value,
    index,
    isSelected,
    setIsSelected,
    setFieldValue,
    onEnter,
    isColorPicker,
    metaDataTextColorValue
}: OrderedListItemProps) => {
    const [textValue, setTextValue] = React.useState<string>(
        typeof value === "string" ? value : value.value
    );
    const [allowEnter, setAllowEnter] = React.useState(false);
    const [showColorPicker, setShowColorPicker] = React.useState(false);
    const [color, setColor] = React.useState<string>(metaDataTextColorValue !== undefined ? metaDataTextColorValue?.hexColor : "#d4e7f6");

    React.useEffect(() => {
        if (allowEnter) {
            onEnter();
            setAllowEnter(false);
        }
    }, [allowEnter]);

    const showColorPickerHandler = () => {
        setShowColorPicker(!showColorPicker);
    }

    const saveColor = (colorHex) => {
        const timer = setTimeout(() => {
            setFieldValue(index, colorHex, textValue);
        }, 500);

        return () => clearTimeout(timer);
    }

    const updateColor = React.useCallback((ev: any, colorObj: IColor) => {
        const hexColor: string = `#${colorObj.hex}`;
        setColor(hexColor);
        saveColor(hexColor);
    }, []);

    return (
        <Stack>
            <Stack
                horizontal
                verticalAlign="center"
                className={isSelected ? orderedListStyles.selected : orderedListStyles.standard}
                tokens={{ childrenGap: 10 }}
            >
                <Checkbox checked={isSelected} onChange={(e, isChecked) => {
                    setIsSelected(index, isChecked)
                }}></Checkbox>
                {!isColorPicker ? <>
                    <TextField
                        value={textValue}
                        styles={{
                            root: {
                                width: "100%"
                            }
                        }}
                        onChange={(_, nv) => {
                            setTextValue(nv);
                        }}
                        onBlur={() => {
                            setFieldValue(index, textValue.trim());
                        }}
                        onKeyDown={(event) => {
                            if (event.code === "Enter") {
                                setTextValue(event?.currentTarget?.value.trim() ?? textValue.trim());
                                setFieldValue(index, event?.currentTarget?.value.trim() ?? textValue.trim());
                                setAllowEnter(true);
                            }
                        }}
                    />
                </> :
                    <>
                        <div style={{ ...pillStyle, backgroundColor: color }}>
                            <span style={{ color: getTextColor(color) }}>{textValue}</span>
                        </div>
                        <IconButton iconProps={{ iconName: showColorPicker ? 'ColorSolid' : 'Color' }} onClick={showColorPickerHandler} />
                    </>
                }
            </Stack>
            {showColorPicker &&
                <Stack tokens={{ childrenGap: 10 }} horizontalAlign="center">
                    <ColorPicker
                        color={color}
                        onChange={updateColor}
                        alphaType={"none"}
                        showPreview
                        styles={colorPickerStyles}
                        strings={{
                            hueAriaLabel: 'Hue',
                        }}
                    />
                    <DefaultButton text="Close" onClick={showColorPickerHandler} />̣
                </Stack>}
        </Stack>
    )
}

export default OrderedListItem;